import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';

const ProductImageLightbox = ({
    isOpen,
    onClose,
    images = [],
    currentIndex = 0,
    onIndexChange,
    productName = "Product"
}) => {
    const [activeIndex, setActiveIndex] = useState(currentIndex);
    const [zoom, setZoom] = useState(1);
    const [pan, setPan] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

    // Touch swipe handling for mobile
    const touchStartX = useRef(0);
    const touchEndX = useRef(0);
    const containerRef = useRef(null);

    // Keep activeIndex in sync with prop currentIndex
    useEffect(() => {
        if (isOpen) {
            setActiveIndex(currentIndex);
            setZoom(1);
            setPan({ x: 0, y: 0 });
        }
    }, [currentIndex, isOpen]);

    // Handle index changes
    const changeImage = useCallback((newIndex) => {
        if (newIndex < 0 || newIndex >= images.length) return;
        setActiveIndex(newIndex);
        setZoom(1);
        setPan({ x: 0, y: 0 });
        if (onIndexChange) {
            onIndexChange(newIndex);
        }
    }, [images.length, onIndexChange]);

    const handlePrev = useCallback((e) => {
        if (e) e.stopPropagation();
        const prevIndex = (activeIndex - 1 + images.length) % images.length;
        changeImage(prevIndex);
    }, [activeIndex, images.length, changeImage]);

    const handleNext = useCallback((e) => {
        if (e) e.stopPropagation();
        const nextIndex = (activeIndex + 1) % images.length;
        changeImage(nextIndex);
    }, [activeIndex, images.length, changeImage]);

    const handleZoomIn = useCallback((e) => {
        if (e) e.stopPropagation();
        setZoom((prev) => Math.min(prev + 0.5, 3));
    }, []);

    const handleZoomOut = useCallback((e) => {
        if (e) e.stopPropagation();
        setZoom((prev) => {
            const next = Math.max(prev - 0.5, 1);
            if (next === 1) setPan({ x: 0, y: 0 });
            return next;
        });
    }, []);

    const handleResetZoom = useCallback((e) => {
        if (e) e.stopPropagation();
        setZoom(1);
        setPan({ x: 0, y: 0 });
    }, []);

    const toggleZoom = useCallback((e) => {
        if (e) e.stopPropagation();
        if (zoom > 1) {
            handleResetZoom();
        } else {
            setZoom(2);
        }
    }, [zoom, handleResetZoom]);

    // Body scroll lock & Keyboard shortcuts
    useEffect(() => {
        if (!isOpen) return;

        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                if (zoom > 1) {
                    handleResetZoom();
                } else {
                    onClose();
                }
            } else if (e.key === 'ArrowLeft') {
                if (images.length > 1) handlePrev();
            } else if (e.key === 'ArrowRight') {
                if (images.length > 1) handleNext();
            } else if (e.key === '+' || e.key === '=') {
                handleZoomIn();
            } else if (e.key === '-' || e.key === '_') {
                handleZoomOut();
            } else if (e.key === '0') {
                handleResetZoom();
            }
        };

        window.addEventListener('keydown', handleKeyDown);

        return () => {
            document.body.style.overflow = originalOverflow;
            window.removeEventListener('keydown', handleKeyDown);
            if (document.fullscreenElement) {
                document.exitFullscreen().catch(() => {});
            }
        };
    }, [isOpen, zoom, images.length, handlePrev, handleNext, handleZoomIn, handleZoomOut, handleResetZoom, onClose]);

    // Pan dragging when zoomed
    const handleMouseDown = (e) => {
        if (zoom <= 1) return;
        setIsDragging(true);
        setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
    };

    const handleMouseMove = (e) => {
        if (!isDragging || zoom <= 1) return;
        setPan({
            x: e.clientX - dragStart.x,
            y: e.clientY - dragStart.y
        });
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    // Mobile touch swipe handling
    const handleTouchStart = (e) => {
        touchStartX.current = e.touches[0].clientX;
        touchEndX.current = e.touches[0].clientX;
    };

    const handleTouchMove = (e) => {
        touchEndX.current = e.touches[0].clientX;
    };

    const handleTouchEnd = () => {
        if (zoom > 1) return; // Disable swipe image flip when zoomed in
        const diffX = touchStartX.current - touchEndX.current;
        const threshold = 50;
        if (diffX > threshold) {
            handleNext();
        } else if (diffX < -threshold) {
            handlePrev();
        }
    };

    if (!isOpen || images.length === 0) return null;

    const currentImage = images[activeIndex] || images[0];

    return createPortal(
        <div
            ref={containerRef}
            className="product-lightbox-overlay"
            onClick={onClose}
            role="dialog"
            aria-modal="true"
            aria-label="Product Image Fullscreen Viewer"
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                backgroundColor: 'rgba(10, 15, 24, 0.94)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                zIndex: 9999999,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                userSelect: 'none',
                overflow: 'hidden',
                animation: 'lightboxFadeIn 0.2s ease-out'
            }}
        >
            <style>{`
                @keyframes lightboxFadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                .lightbox-control-btn {
                    background: rgba(255, 255, 255, 0.12);
                    border: 1px solid rgba(255, 255, 255, 0.18);
                    color: #FFFFFF;
                    width: 40px;
                    height: 40px;
                    border-radius: 10px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 18px;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }
                .lightbox-control-btn:hover {
                    background: rgba(255, 255, 255, 0.25);
                    border-color: rgba(255, 255, 255, 0.35);
                    color: #FFFFFF;
                    transform: translateY(-1px);
                }
                .lightbox-control-btn:active {
                    transform: translateY(1px);
                }
                .lightbox-nav-btn {
                    background: rgba(20, 26, 38, 0.7);
                    border: 1px solid rgba(255, 255, 255, 0.2);
                    color: #FFFFFF;
                    width: 50px;
                    height: 50px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 24px;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);
                }
                .lightbox-nav-btn:hover {
                    background: rgba(47, 168, 79, 0.9);
                    border-color: #2FA84F;
                    color: #FFFFFF;
                    transform: scale(1.08);
                }
                .lightbox-thumb {
                    width: 60px;
                    height: 60px;
                    border-radius: 10px;
                    border: 2px solid rgba(255, 255, 255, 0.2);
                    background: #FFFFFF;
                    padding: 4px;
                    cursor: pointer;
                    flex-shrink: 0;
                    transition: all 0.2s ease;
                }
                .lightbox-thumb.active {
                    border-color: #2FA84F;
                    box-shadow: 0 0 12px rgba(47, 168, 79, 0.6);
                    transform: scale(1.08);
                }
                .lightbox-thumb:hover {
                    border-color: #2FA84F;
                    opacity: 1;
                }
                .lightbox-thumb-strip::-webkit-scrollbar {
                    height: 6px;
                }
                .lightbox-thumb-strip::-webkit-scrollbar-thumb {
                    background: rgba(255, 255, 255, 0.25);
                    border-radius: 4px;
                }
                .lightbox-close-btn {
                    position: absolute;
                    top: 20px;
                    right: 20px;
                    width: 44px;
                    height: 44px;
                    border-radius: 50%;
                    background: rgba(255, 255, 255, 0.15);
                    border: 1px solid rgba(255, 255, 255, 0.25);
                    color: #FFFFFF;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 22px;
                    cursor: pointer;
                    z-index: 100;
                    transition: all 0.2s ease;
                    backdrop-filter: blur(8px);
                    -webkit-backdrop-filter: blur(8px);
                    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
                }
                .lightbox-close-btn:hover {
                    background: rgba(239, 68, 68, 0.85);
                    border-color: #EF4444;
                    transform: scale(1.08);
                    color: #FFFFFF;
                }
            `}</style>

            {/* Floating Close Button */}
            <button
                type="button"
                onClick={onClose}
                className="lightbox-close-btn"
                aria-label="Close viewer"
                title="Close (Esc)"
            >
                <i className="ph ph-x" />
            </button>

            {/* Main Image Center Stage */}
            <div
                style={{
                    position: 'relative',
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    padding: '12px 24px',
                    cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'zoom-in'
                }}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
            >
                {/* Previous button */}
                {images.length > 1 && (
                    <button
                        type="button"
                        onClick={handlePrev}
                        className="lightbox-nav-btn"
                        aria-label="Previous image"
                        title="Previous image (Left Arrow)"
                        style={{
                            position: 'absolute',
                            left: '24px',
                            zIndex: 20
                        }}
                    >
                        <i className="ph ph-caret-left" />
                    </button>
                )}

                {/* Displayed Image */}
                <div
                    onClick={toggleZoom}
                    style={{
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        maxWidth: '92vw',
                        maxHeight: images.length > 1 ? 'calc(100vh - 130px)' : 'calc(100vh - 60px)',
                        transform: `scale(${zoom}) translate(${pan.x / zoom}px, ${pan.y / zoom}px)`,
                        transition: isDragging ? 'none' : 'transform 0.2s ease-out',
                        transformOrigin: 'center center'
                    }}
                >
                    <img
                        src={currentImage}
                        alt={`${productName} - Fullscreen View ${activeIndex + 1}`}
                        draggable={false}
                        style={{
                            maxWidth: '100%',
                            maxHeight: images.length > 1 ? 'calc(100vh - 130px)' : 'calc(100vh - 60px)',
                            objectFit: 'contain',
                            borderRadius: '12px',
                            backgroundColor: '#FFFFFF',
                            padding: '16px',
                            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.6)',
                            pointerEvents: zoom > 1 ? 'auto' : 'auto'
                        }}
                    />
                </div>

                {/* Next button */}
                {images.length > 1 && (
                    <button
                        type="button"
                        onClick={handleNext}
                        className="lightbox-nav-btn"
                        aria-label="Next image"
                        title="Next image (Right Arrow)"
                        style={{
                            position: 'absolute',
                            right: '24px',
                            zIndex: 20
                        }}
                    >
                        <i className="ph ph-caret-right" />
                    </button>
                )}
            </div>

            {/* Bottom thumbnail strip (only if multiple images) */}
            {images.length > 1 && (
                <div
                    onClick={(e) => e.stopPropagation()}
                    style={{
                        padding: '8px 24px 16px',
                        display: 'flex',
                        justifyContent: 'center',
                        zIndex: 10
                    }}
                >
                    <div
                        className="lightbox-thumb-strip"
                        style={{
                            display: 'flex',
                            gap: '12px',
                            maxWidth: '90vw',
                            overflowX: 'auto',
                            padding: '4px 8px 8px',
                            justifyContent: 'center'
                        }}
                    >
                        {images.map((img, idx) => (
                            <button
                                key={idx}
                                type="button"
                                onClick={() => changeImage(idx)}
                                className={`lightbox-thumb ${idx === activeIndex ? 'active' : ''}`}
                                aria-label={`View photo ${idx + 1}`}
                                title={`Photo ${idx + 1}`}
                            >
                                <img
                                    src={img}
                                    alt={`Thumb ${idx + 1}`}
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'contain',
                                        borderRadius: '6px'
                                    }}
                                />
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>,
        document.body
    );
};

export default ProductImageLightbox;
